import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Download, 
  Upload,
  Eye,
  Settings,
  Building2,
  Shield,
  Layers,
  Palette,
  Globe,
  Lock,
  Unlock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Crown,
  Sparkles
} from 'lucide-react';
import BaseTemplateBuilder from './BaseTemplateBuilder';

interface BaseTemplate {
  id: string;
  name: string;
  description: string;
  category: 'education' | 'professional' | 'government' | 'healthcare' | 'general';
  templateType: 'base' | 'organization' | 'custom';
  width: number;
  height: number;
  backgroundColor: string;
  backgroundImage?: string;
  fieldPositions: FieldPosition[];
  schemaRequirements: SchemaRequirement[];
  isGlobal: boolean;
  isMandatory: boolean;
  allowedOrganizations: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  usageCount: number;
  version: string;
  status: 'active' | 'draft' | 'deprecated';
}

interface FieldPosition {
  id: string;
  fieldName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color: string;
  isRequired: boolean;
  isLocked: boolean; // Super Admin can lock field positions
}

interface SchemaRequirement {
  fieldName: string;
  fieldType: string;
  required: boolean;
  description: string;
}

interface TemplateAssignment {
  organizationId: string;
  organizationName: string;
  templateId: string;
  templateName: string;
  assignedAt: string;
  assignedBy: string;
  status: 'active' | 'pending' | 'rejected';
  isDefault: boolean;
}

export default function BaseTemplateManagement() {
  const [baseTemplates, setBaseTemplates] = useState<BaseTemplate[]>([
    {
      id: 'base_template_1',
      name: 'Global University Diploma',
      description: 'Standard template for all university credentials across the platform',
      category: 'education',
      templateType: 'base',
      width: 800,
      height: 600,
      backgroundColor: '#ffffff',
      backgroundImage: '/images/diploma-bg.png',
      fieldPositions: [
        {
          id: 'field_1',
          fieldName: 'recipientName',
          x: 200,
          y: 250,
          width: 400,
          height: 40,
          fontSize: 24,
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#1e40af',
          isRequired: true,
          isLocked: true
        },
        {
          id: 'field_2',
          fieldName: 'credentialTitle',
          x: 200,
          y: 300,
          width: 400,
          height: 30,
          fontSize: 18,
          fontWeight: 'normal',
          textAlign: 'center',
          color: '#374151',
          isRequired: true,
          isLocked: true
        }
      ],
      schemaRequirements: [
        {
          fieldName: 'recipientName',
          fieldType: 'string',
          required: true,
          description: 'Full name of the degree recipient'
        },
        {
          fieldName: 'credentialTitle',
          fieldType: 'string',
          required: true,
          description: 'Name of the degree program'
        }
      ],
      isGlobal: true,
      isMandatory: true,
      allowedOrganizations: ['*'], // All organizations
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      createdBy: 'super_admin',
      usageCount: 156,
      version: '1.0.0',
      status: 'active'
    },
    {
      id: 'base_template_2',
      name: 'Professional Certification',
      description: 'Standard template for professional certifications',
      category: 'professional',
      templateType: 'base',
      width: 800,
      height: 600,
      backgroundColor: '#f8fafc',
      fieldPositions: [
        {
          id: 'field_1',
          fieldName: 'recipientName',
          x: 150,
          y: 220,
          width: 500,
          height: 35,
          fontSize: 22,
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#059669',
          isRequired: true,
          isLocked: true
        }
      ],
      schemaRequirements: [
        {
          fieldName: 'recipientName',
          fieldType: 'string',
          required: true,
          description: 'Name of the certificate recipient'
        }
      ],
      isGlobal: true,
      isMandatory: false,
      allowedOrganizations: ['*'],
      createdAt: '2024-01-15T00:00:00Z',
      updatedAt: '2024-01-15T00:00:00Z',
      createdBy: 'super_admin',
      usageCount: 89,
      version: '1.0.0',
      status: 'active'
    }
  ]);

  const [templateAssignments, setTemplateAssignments] = useState<TemplateAssignment[]>([
    {
      organizationId: 'tenant1',
      organizationName: 'State University',
      templateId: 'base_template_1',
      templateName: 'Global University Diploma',
      assignedAt: '2024-01-01T00:00:00Z',
      assignedBy: 'super_admin',
      status: 'active',
      isDefault: true
    },
    {
      organizationId: 'tenant3',
      organizationName: 'National Real Estate Board',
      templateId: 'base_template_2',
      templateName: 'Professional Certification',
      assignedAt: '2024-01-15T00:00:00Z',
      assignedBy: 'super_admin',
      status: 'active',
      isDefault: false
    }
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState<BaseTemplate | null>(null);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');

  const mockOrganizations = [
    { id: 'tenant1', name: 'State University' },
    { id: 'tenant2', name: 'Tech Certification Co' },
    { id: 'tenant3', name: 'National Real Estate Board' },
    { id: 'tenant4', name: 'Healthcare Credentials Authority' },
    { id: 'tenant5', name: 'American Livestock Association' }
  ];

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateBuilder(true);
  };

  const handleEditTemplate = (template: BaseTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateBuilder(true);
  };

  const handleSaveTemplate = (template: BaseTemplate) => {
    if (selectedTemplate) {
      // Update existing template
      setBaseTemplates(prev => 
        prev.map(t => t.id === template.id ? template : t)
      );
    } else {
      // Create new template
      const newTemplate: BaseTemplate = {
        ...template,
        id: `base_template_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'super_admin',
        usageCount: 0
      };
      setBaseTemplates(prev => [...prev, newTemplate]);
    }
    setShowTemplateBuilder(false);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = (templateId: string) => {
    setBaseTemplates(prev => prev.filter(t => t.id !== templateId));
    setTemplateAssignments(prev => prev.filter(a => a.templateId !== templateId));
  };

  const handleAssignTemplate = (templateId: string, organizationIds: string[]) => {
    const template = baseTemplates.find(t => t.id === templateId);
    if (!template) return;

    const newAssignments: TemplateAssignment[] = organizationIds.map(orgId => {
      const org = mockOrganizations.find(o => o.id === orgId);
      return {
        organizationId: orgId,
        organizationName: org?.name || 'Unknown',
        templateId: templateId,
        templateName: template.name,
        assignedAt: new Date().toISOString(),
        assignedBy: 'super_admin',
        status: 'active' as const,
        isDefault: false
      };
    });

    setTemplateAssignments(prev => [...prev, ...newAssignments]);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'education': return 'bg-blue-100 text-blue-800';
      case 'professional': return 'bg-green-100 text-green-800';
      case 'government': return 'bg-purple-100 text-purple-800';
      case 'healthcare': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'deprecated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTemplateTypeIcon = (type: string) => {
    switch (type) {
      case 'base': return <Crown className="h-4 w-4" />;
      case 'organization': return <Building2 className="h-4 w-4" />;
      case 'custom': return <Palette className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (showTemplateBuilder) {
    return (
      <BaseTemplateBuilder
        template={selectedTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setShowTemplateBuilder(false);
          setSelectedTemplate(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal-ink">
            Base Template Management
          </h2>
          <p className="text-warm-slate">
            Manage global credential templates that all organizations can use
          </p>
        </div>
        <Button onClick={handleCreateTemplate} className="credential-capital-button-primary">
          <Plus className="h-4 w-4 mr-2" />
          Create Base Template
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates">
            <Layers className="h-4 w-4 mr-2" />
            Base Templates
          </TabsTrigger>
          <TabsTrigger value="assignments">
            <Users className="h-4 w-4 mr-2" />
            Template Assignments
          </TabsTrigger>
          <TabsTrigger value="standards">
            <Shield className="h-4 w-4 mr-2" />
            Global Standards
          </TabsTrigger>
        </TabsList>

        {/* Base Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Global Base Templates</CardTitle>
              <CardDescription>
                Master templates that define the visual and structural standards for all credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {baseTemplates.map((template) => (
                  <Card key={template.id} className="border">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 rounded-lg bg-electric-steel/10">
                            {getTemplateTypeIcon(template.templateType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-semibold text-charcoal-ink">{template.name}</h3>
                              {template.isGlobal && (
                                <Badge className="bg-blue-100 text-blue-800">
                                  <Globe className="h-3 w-3 mr-1" />
                                  Global
                                </Badge>
                              )}
                              {template.isMandatory && (
                                <Badge className="bg-red-100 text-red-800">
                                  <Shield className="h-3 w-3 mr-1" />
                                  Mandatory
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-warm-slate mb-3">{template.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-warm-slate">
                              <Badge className={getCategoryColor(template.category)}>
                                {template.category}
                              </Badge>
                              <Badge className={getStatusColor(template.status)}>
                                {template.status}
                              </Badge>
                              <span>Version: {template.version}</span>
                              <span>Usage: {template.usageCount} credentials</span>
                              <span>Size: {template.width}x{template.height}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAssignmentDialog(true)}
                          >
                            <Users className="h-4 w-4 mr-2" />
                            Assign
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Template Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Template Assignments</CardTitle>
              <CardDescription>
                Manage which organizations have access to specific base templates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {templateAssignments.map((assignment, index) => (
                  <Card key={index} className="border">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 rounded-lg bg-cool-gray-100">
                            <Building2 className="h-5 w-5 text-electric-steel" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-charcoal-ink">
                              {assignment.organizationName}
                            </h4>
                            <p className="text-sm text-warm-slate">
                              Template: {assignment.templateName}
                            </p>
                            <p className="text-xs text-warm-slate">
                              Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <Badge className={getStatusColor(assignment.status)}>
                              {assignment.status}
                            </Badge>
                            {assignment.isDefault && (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <Sparkles className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <Button variant="outline" size="sm">
                            <Settings className="h-4 w-4 mr-2" />
                            Manage
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Standards Tab */}
        <TabsContent value="standards" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Global Design Standards</CardTitle>
              <CardDescription>
                Define platform-wide design standards and requirements for all credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-charcoal-ink">Template Requirements</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Minimum Template Size</span>
                        <span className="font-medium">600x400px</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Maximum Template Size</span>
                        <span className="font-medium">1200x800px</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Required Fields</span>
                        <span className="font-medium">Recipient Name, Issue Date</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Logo Requirements</span>
                        <span className="font-medium">Mandatory</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-semibold text-charcoal-ink">Security Standards</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">QR Code</span>
                        <span className="font-medium">Mandatory</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Verification URL</span>
                        <span className="font-medium">Required</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Digital Signature</span>
                        <span className="font-medium">W3C Compliant</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                        <span className="text-warm-slate">Watermark</span>
                        <span className="font-medium">Optional</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Button className="credential-capital-button-primary">
                    Update Standards
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Assignment Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Template to Organizations</DialogTitle>
            <DialogDescription>
              Select organizations that should have access to this template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {mockOrganizations.map((org) => (
                <Card key={org.id} className="border cursor-pointer hover:bg-cool-gray-100">
                  <CardContent className="pt-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-electric-steel/10">
                        <Building2 className="h-4 w-4 text-electric-steel" />
                      </div>
                      <div>
                        <h4 className="font-medium text-charcoal-ink">{org.name}</h4>
                        <p className="text-sm text-warm-slate">ID: {org.id}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex space-x-2">
              <Button className="credential-capital-button-primary">
                Assign Selected
              </Button>
              <Button variant="outline" onClick={() => setShowAssignmentDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}